// View state ⇄ URL (F06). Schema version v=1 — old links must keep resolving forever.
import { LAYERS, LAYER_BY_ID, initialLayers, type LayerId, type LayerState } from '../viewer/layers';
import type { Body, CameraState, IsolateMode, Side } from '../viewer/Viewer';

export const STATE_VERSION = 1;

export interface ViewState {
  selected: { id: string; side?: Side } | null;
  camera: CameraState | null;
  layers: Record<LayerId, LayerState>;
  hidden: string[];
  isolate: { mode: IsolateMode; ids: string[]; region?: string } | null;
  tour?: { id: string; stop: number } | null;
  /** Which body (F22). In links only as `b=f`; a link with view state but no `b` is the male body. */
  body?: Body;
}

const LAYER_FROM_SHORT = Object.fromEntries(LAYERS.map((l) => [l.short, l.id])) as Record<string, LayerId>;
const STATE_CODE: Record<LayerState, string> = { on: 'o', ghost: 'g', off: 'x' };
const CODE_STATE: Record<string, LayerState> = { o: 'on', g: 'ghost', x: 'off' };
const SLUG = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
const q = (n: number) => (Math.round(n * 1000) / 1000).toString();

export function emptyState(): ViewState {
  return { selected: null, camera: null, layers: initialLayers(), hidden: [], isolate: null, tour: null };
}

/** Serialises a view to path + query, e.g. `/s/ulnar-nerve?v=1&cam=…&L=mu:g`. */
export function toUrl(s: ViewState): string {
  const p = new URLSearchParams();
  p.set('v', String(STATE_VERSION));
  if (s.selected?.side && s.selected.side !== 'm') p.set('side', s.selected.side);
  if (s.camera) p.set('cam', [...s.camera.position, ...s.camera.target].map(q).join(','));
  const def = initialLayers();
  const layers = LAYERS.filter((l) => s.layers[l.id] !== def[l.id]).map((l) => `${l.short}:${STATE_CODE[s.layers[l.id]]}`);
  if (layers.length) p.set('L', layers.join(','));
  if (s.isolate) p.set('iso', [s.isolate.mode, s.isolate.region ?? '', ...s.isolate.ids].join(','));
  if (s.hidden.length) p.set('hide', [...s.hidden].sort().join(','));
  if (s.tour) p.set('tour', `${s.tour.id}:${s.tour.stop}`);
  if (s.body === 'f') p.set('b', 'f');
  const path = s.tour && !s.selected ? `/t/${s.tour.id}` : s.selected ? `/s/${s.selected.id}` : '/';
  return `${path}?${p.toString().replace(/%2C/g, ',').replace(/%3A/g, ':')}`;
}

/** Parses a URL (path + search). Unknown or malformed parts are ignored, never thrown. */
export function fromUrl(pathname: string, search: string, knownIds?: (id: string) => boolean): ViewState & { unknownSlug?: string } {
  const s = emptyState() as ViewState & { unknownSlug?: string };
  const p = new URLSearchParams(search);
  const m = pathname.match(/^\/s\/([^/?#]+)\/?$/);
  if (m) {
    const slug = decodeURIComponent(m[1]).toLowerCase();
    if (SLUG.test(slug) && (!knownIds || knownIds(slug))) {
      const side = p.get('side');
      s.selected = { id: slug, side: side === 'l' || side === 'r' ? side : undefined };
    } else s.unknownSlug = slug;
  }
  const t = pathname.match(/^\/t\/([a-z0-9-]+)\/?$/);
  const tourParam = p.get('tour');
  if (tourParam) {
    const [id, stop] = tourParam.split(':');
    if (SLUG.test(id)) s.tour = { id, stop: Math.max(0, parseInt(stop, 10) || 0) };
  } else if (t) s.tour = { id: t[1], stop: 0 };

  const cam = p.get('cam')?.split(',').map(Number);
  if (cam && cam.length === 6 && cam.every((n) => Number.isFinite(n) && Math.abs(n) < 100)) {
    s.camera = { position: [cam[0], cam[1], cam[2]], target: [cam[3], cam[4], cam[5]] };
  }
  for (const part of (p.get('L') ?? '').split(',').filter(Boolean)) {
    const [short, code] = part.split(':');
    const id = LAYER_FROM_SHORT[short];
    if (id && CODE_STATE[code]) s.layers[id] = CODE_STATE[code];
  }
  const ok = (id: string) => SLUG.test(id) && (!knownIds || knownIds(id));
  const iso = p.get('iso')?.split(',');
  if (iso && ['alone', 'skeleton', 'region'].includes(iso[0])) {
    const ids = iso.slice(2).filter(ok);
    if (ids.length || iso[0] === 'region') s.isolate = { mode: iso[0] as IsolateMode, ids, region: iso[1] || undefined };
  }
  s.hidden = (p.get('hide') ?? '').split(',').filter(ok);
  // Links that carry view state are explicit about the body (absent = male, as before F22). A bare URL leaves it
  // to the reader's remembered preference.
  if (p.get('b') === 'f') s.body = 'f';
  else if (p.has('v')) s.body = 'm';
  return s;
}

export const layerLabel = (id: LayerId) => LAYER_BY_ID[id].label;
